import { track } from '@vercel/analytics';
import { useCallback } from 'react';
import * as GA from '../utils/googleAnalytics';

export type AnalyticsEvent =
  | 'project_view'
  | 'case_study_open'
  | 'case_study_close'
  | 'email_copy'
  | 'external_link_click'
  | 'navigation_click'
  | 'sidebar_open'
  | 'sidebar_close'
  | 'modal_navigation'
  | 'contact_interaction'
  | 'easter_egg_visit'
  | 'easter_egg_solution_reveal';

export interface AnalyticsEventData {
  project?: string;
  link_type?: 'github' | 'linkedin' | 'demo' | 'email';
  section?: string;
  modal_action?: 'next' | 'prev' | 'close';
  interaction_type?: string;
  page_location?: string;
  device_type?: 'mobile' | 'tablet' | 'desktop';
  page?: string;
}

export const useAnalytics = () => {
  const trackEvent = useCallback((
    event: AnalyticsEvent,
    data?: AnalyticsEventData
  ) => {
    try {
      // Only track in production or when analytics is explicitly enabled
      if (
        process.env.NODE_ENV === 'production' ||
        process.env.REACT_APP_VERCEL_ANALYTICS_ID
      ) {
        // Add device type detection
        const deviceType = getDeviceType();
        const enrichedData = {
          ...data,
          device_type: deviceType,
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent,
          screen_resolution: `${window.screen.width}x${window.screen.height}`,
          viewport_size: `${window.innerWidth}x${window.innerHeight}`
        };

        // Track to Vercel Analytics
        track(event, enrichedData);

        // Track to Google Analytics
        GA.trackEvent(event, enrichedData);
      }
    } catch (error) {
      console.warn('Analytics tracking failed:', error);
    }
  }, []);

  const trackPageView = useCallback((page: string) => {
    trackEvent('navigation_click', {
      section: page,
      page_location: window.location.pathname
    });
    // Also track as page view in GA
    GA.trackPageView(page, document.title);
  }, [trackEvent]);

  const trackProjectView = useCallback((projectId: string, viewType: 'preview' | 'case_study' = 'preview') => {
    trackEvent('project_view', {
      project: projectId,
      interaction_type: viewType,
      page_location: window.location.pathname
    });
    // Also track directly to GA
    GA.trackProjectView(projectId, viewType);
  }, [trackEvent]);

  const trackCaseStudyOpen = useCallback((projectId: string) => {
    trackEvent('case_study_open', {
      project: projectId,
      page_location: window.location.pathname
    });
    // Track virtual page view for case study
    GA.trackPageView(`/project/${projectId}`, `Case Study: ${projectId}`);
    GA.trackCaseStudyOpen(projectId);
  }, [trackEvent]);

  const trackCaseStudyClose = useCallback((projectId: string) => {
    trackEvent('case_study_close', {
      project: projectId,
      page_location: window.location.pathname
    });
    GA.trackCaseStudyClose(projectId);
  }, [trackEvent]);

  const trackEmailCopy = useCallback((success: boolean) => {
    trackEvent('email_copy', {
      interaction_type: success ? 'success' : 'failed',
      page_location: window.location.pathname
    });
    GA.trackEmailCopy(success);
  }, [trackEvent]);

  const trackExternalLink = useCallback((linkType: 'github' | 'linkedin' | 'demo', projectId?: string) => {
    trackEvent('external_link_click', {
      link_type: linkType,
      project: projectId,
      page_location: window.location.pathname
    });
    GA.trackExternalLink(linkType, projectId);
  }, [trackEvent]);

  const trackSidebarToggle = useCallback((action: 'open' | 'close') => {
    trackEvent(action === 'open' ? 'sidebar_open' : 'sidebar_close', {
      page_location: window.location.pathname
    });
    GA.trackSidebarToggle(action);
  }, [trackEvent]);

  const trackModalNavigation = useCallback((action: 'next' | 'prev' | 'close', projectId?: string) => {
    trackEvent('modal_navigation', {
      modal_action: action,
      project: projectId,
      page_location: window.location.pathname
    });
    GA.trackModalNavigation(action, projectId);
  }, [trackEvent]);

  const trackContactInteraction = useCallback((interactionType: string) => {
    trackEvent('contact_interaction', {
      interaction_type: interactionType,
      page_location: window.location.pathname
    });
  }, [trackEvent]);

  return {
    trackEvent,
    trackPageView,
    trackProjectView,
    trackCaseStudyOpen,
    trackCaseStudyClose,
    trackEmailCopy,
    trackExternalLink,
    trackSidebarToggle,
    trackModalNavigation,
    trackContactInteraction
  };
};

// Helper function to detect device type
function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

// Helper function to track performance metrics
export const trackPerformanceMetrics = () => {
  if ('performance' in window && 'getEntriesByType' in performance) {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    
    if (navigation) {
      track('performance_metrics', {
        load_time: Math.round(navigation.loadEventEnd - navigation.fetchStart),
        dom_content_loaded: Math.round(navigation.domContentLoadedEventEnd - navigation.fetchStart),
        first_byte: Math.round(navigation.responseStart - navigation.fetchStart),
        dns_lookup: Math.round(navigation.domainLookupEnd - navigation.domainLookupStart),
        connection_time: Math.round(navigation.connectEnd - navigation.connectStart),
        page_size: navigation.transferSize || 0,
        device_type: getDeviceType()
      });
    }
  }

  // Track Core Web Vitals if available
  if ('web-vitals' in window) {
    // This would integrate with the existing web-vitals package
    // The SpeedInsights component will handle this automatically
  }
};